import React, { useState, useEffect } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import Select from 'react-select';
import axios from "axios";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  ProgressBar,
  Modal,
  Badge,
  Table
} from "react-bootstrap";
import {
  CheckCircle,
  Save,
  Briefcase,
  Folder,
  Tag,
  FileText,
  Check,
  X,
  ExclamationCircle,
  Building,
  Receipt,
  InfoCircle,
  ArrowLeft,
  PlusCircle,
  Eye,
  Calendar
} from "react-bootstrap-icons";

// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_API_URL;
const ACTIVITIES_ENDPOINT = `${API_BASE_URL}/activites`;
const PARENT_CATEGORIES_ENDPOINT = `${API_BASE_URL}/usager-categories`;

export default function AddActivity() {
  const navigate = useNavigate();

  // États du formulaire
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    code_category: ""
  });

  // États des données
  const [parentCategories, setParentCategories] = useState([]);
  
  // États de chargement
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // États d'erreur
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState(null);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });
  
  // Progression du formulaire
  const [formProgress, setFormProgress] = useState(0);
  
  // Modals
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);

  // Obtenir le token d'authentification
  const getAuthToken = () => {
    return sessionStorage.getItem("authToken");
  };

  // Headers pour les requêtes API
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Charger les catégories parentes
  const fetchParentCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(PARENT_CATEGORIES_ENDPOINT, {
        headers: getHeaders(),
      });
      
      if (response.data && Array.isArray(response.data.data)) {
        setParentCategories(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setParentCategories(response.data);
      } else {
        setParentCategories([]);
      }
    } catch (error) {
      console.error("Erreur chargement catégories parentes:", error);
      setParentCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchParentCategories();
  }, []);

  // Calculer la progression du formulaire
  useEffect(() => {
    const fieldsWeight = {
      name: { weight: 50, required: true },
      code_category: { weight: 50, required: true }
    };
    
    let totalProgress = 0;
    Object.entries(fieldsWeight).forEach(([field, config]) => {
      const value = formData[field];
      let isFilled = false;
      if (field === 'code_category') {
        isFilled = value && value !== "" && value !== 0;
      } else {
        isFilled = value && typeof value === 'string' && value.trim().length > 0;
      }
      if (isFilled) totalProgress += config.weight;
    });
    setFormProgress(Math.min(100, totalProgress));
  }, [formData]);

  // Options pour react-select
  const parentCategoryOptions = React.useMemo(() => {
    return parentCategories.map(cat => ({
      value: cat.code_category || cat.id,
      label: cat.category_name || cat.name
    }));
  }, [parentCategories]);

  const selectedParentCategory = React.useMemo(() => {
    if (!formData.code_category) return null;
    return parentCategoryOptions.find(opt => opt.value === formData.code_category);
  }, [formData.code_category, parentCategoryOptions]);

  // Gestion des changements de formulaire
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name?.trim()) {
      newErrors.name = "Le nom de l'activité est requis";
    }
    
    if (!formData.code_category) {
      newErrors.code_category = "Veuillez sélectionner une catégorie";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Affichage des alertes
  const showAlert = (message, type = 'danger', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) setTimeout(() => setAlertMessage({ show: false, message: '', type: 'info' }), duration);
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setGlobalError(null);
    
    try {
      const payload = {
        name: formData.name,
        description: formData.description || "",
        code_category: Number(formData.code_category),
        date_activite: new Date().toISOString().split('T')[0]
      };
      
      const response = await axios.post(
        ACTIVITIES_ENDPOINT,
        payload,
        { headers: getHeaders() }
      );
      
      setApiResponse(response.data);
      setShowResponseModal(true);
      
    } catch (error) {
      console.error("Erreur lors de l'ajout:", error);
      
      if (error.response) {
        if (error.response.status === 401) {
          setGlobalError("Session expirée. Veuillez vous reconnecter.");
        } else if (error.response.status === 403) {
          setGlobalError("Vous n'avez pas les droits pour ajouter une activité.");
        } else if (error.response.status === 422 && error.response.data.errors) {
          setErrors(error.response.data.errors);
          setGlobalError("Veuillez corriger les erreurs ci-dessous.");
        } else {
          setGlobalError(error.response.data?.message || "Erreur lors de la création de l'activité");
        }
      } else if (error.request) {
        setGlobalError("Impossible de contacter le serveur. Vérifiez votre connexion.");
      } else {
        setGlobalError(`Erreur: ${error.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <Header />
      
      <div className="main main-app p-3 p-lg-4">
        <div className="container-fluid">
          {/* En-tête */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <ol className="breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><Link to="/admin/dashboard">Tableau de bord</Link></li>
                <li className="breadcrumb-item"><Link to="/agent/contribuables/Activites">Activités</Link></li>
                <li className="breadcrumb-item active">Nouvelle activité</li>
              </ol>
              <h1 className="main-title mb-0 fs-2 fw-bold">Nouvelle Activité</h1>
              <p className="text-muted mt-1 mb-0">Ajoutez une nouvelle activité pour la gestion des usagers</p>
            </div>
            <Button 
              variant="outline-secondary" 
              onClick={() => navigate(-1)}
              className="d-flex align-items-center"
            >
              <ArrowLeft className="me-2" size={16} />
              Retour
            </Button>
          </div>

          {/* Barre de progression */}
          <Card className="border-0 shadow-sm mb-4 bg-white">
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">Progression du formulaire</small>
                <small className="fw-bold text-primary">{formProgress}%</small>
              </div>
              <ProgressBar now={formProgress} variant="primary" className="rounded-pill" style={{ height: '8px' }} />
            </Card.Body>
          </Card>

          {/* Formulaire principal */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-primary text-white d-flex align-items-center py-3">
                  <Briefcase className="me-2" size={20} />
                  <span className="fw-semibold fs-5">Informations de l'activité</span>
                </Card.Header>
                <Card.Body className="p-4 bg-white">
                  <Form onSubmit={handleSubmit}>
                    {/* Section Classification */}
                    <div className="mb-4">
                      <h6 className="text-primary mb-3 pb-2 border-bottom">
                        <Folder className="me-2" size={16} /> Classification
                      </h6>
                      <Row>
                        {/* Catégorie */}
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-medium">
                              Catégorie <span className="text-danger">*</span>
                            </Form.Label>
                            
                            {parentCategories.length === 0 && !loadingCategories ? (
                              <div className="text-center p-4 border rounded-3 bg-light">
                                <Building size={32} className="text-muted mb-2" />
                                <p className="mb-2">Aucune catégorie disponible</p>
                                <Button 
                                  variant="primary" 
                                  size="sm"
                                  onClick={() => navigate('/agent/contribuables/Categories')}
                                >
                                  <PlusCircle className="me-2" size={14} />
                                  Créer une catégorie
                                </Button>
                              </div>
                            ) : (
                              <>
                                <Select
                                  options={parentCategoryOptions}
                                  value={selectedParentCategory}
                                  onChange={(option) => handleChange("code_category", option?.value || "")}
                                  isLoading={loadingCategories}
                                  isClearable
                                  isSearchable
                                  placeholder="Rechercher une catégorie..."
                                  noOptionsMessage={() => "Aucune catégorie trouvée"}
                                  loadingMessage={() => "Chargement..."}
                                  className={errors.code_category ? "is-invalid" : ""}
                                  styles={{
                                    control: (base) => ({ 
                                      ...base, 
                                      padding: '2px 0', 
                                      backgroundColor: '#f8f9fa',
                                      borderColor: errors.code_category ? '#dc3545' : '#dee2e6',
                                      boxShadow: 'none',
                                      '&:hover': { borderColor: '#0d6efd' }
                                    })
                                  }}
                                />
                                {errors.code_category && (
                                  <Form.Text className="text-danger">{errors.code_category}</Form.Text>
                                )}
                              </>
                            )}
                            <Form.Text className="text-muted">
                              Catégorie d'activité pour l'organisation
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    {/* Section Informations principales */}
                    <div className="mb-4">
                      <h6 className="text-primary mb-3 pb-2 border-bottom">
                        <InfoCircle className="me-2" size={16} /> Informations principales
                      </h6>
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-medium">
                              Nom de l'activité <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="position-relative">
                              <Briefcase className="position-absolute text-muted" style={{ top: '12px', left: '15px', zIndex: 0 }} size={18} />
                              <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                                placeholder="Ex: Commerce de détail, Restauration, Services..."
                                className={`py-2 ps-5 bg-light ${errors.name ? 'is-invalid' : ''}`}
                              />
                            </div>
                            {errors.name && (
                              <Form.Text className="text-danger">{errors.name}</Form.Text>
                            )}
                            <Form.Text className="text-muted">
                              Nom descriptif de l'activité
                            </Form.Text>
                          </Form.Group>
                        </Col>

                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-medium">
                              Description
                            </Form.Label>
                            <div className="position-relative">
                              <FileText className="position-absolute text-muted" style={{ top: '15px', left: '15px', zIndex: 0 }} size={18} />
                              <Form.Control
                                as="textarea"
                                rows={4}
                                name="description"
                                value={formData.description}
                                onChange={(e) => handleChange("description", e.target.value)}
                                placeholder="Description détaillée de l'activité..."
                                className="py-2 ps-5 bg-light"
                                style={{ resize: 'vertical' }}
                              />
                            </div>
                            <Form.Text className="text-muted">
                              Description optionnelle mais recommandée
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    {/* Résumé des informations */}
                    {(formData.name || formData.code_category) && (
                      <div className="mb-4 mt-3">
                        <div className="bg-light rounded-3 p-4">
                          <h6 className="fw-semibold mb-3 d-flex align-items-center">
                            <CheckCircle className="me-2 text-success" size={16} />
                            Résumé de l'activité
                          </h6>
                          <Row className="g-2">
                            <Col md={6}>
                              <small className="text-muted d-block">Nom</small>
                              <strong>{formData.name || "—"}</strong>
                            </Col>
                            <Col md={6}>
                              <small className="text-muted d-block">Catégorie</small>
                              <strong>{selectedParentCategory?.label || "—"}</strong>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    )}

                    <hr className="my-4" />
                    
                    <div className="d-flex gap-3 justify-content-end">
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => navigate(-1)}
                        className="d-flex align-items-center px-4 py-2"
                        disabled={submitting}
                      >
                        <X className="me-2" size={16} />
                        Annuler
                      </Button>
                      <Button 
                        type="submit" 
                        variant="success" 
                        className="d-flex align-items-center px-4 py-2"
                        disabled={submitting || !formData.name?.trim() || !formData.code_category}
                      >
                        {submitting ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Création en cours...
                          </>
                        ) : (
                          <>
                            <Save className="me-2" size={16} />
                            Créer l'activité
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>

                  {/* Alerte */}
                  {alertMessage.show && (
                    <div className="mb-4 mt-3">
                      <Alert variant={alertMessage.type} dismissible onClose={() => setAlertMessage({ show: false, message: '', type: 'info' })} className="shadow-sm border-0">
                        <div className="d-flex align-items-center">
                          {alertMessage.type === 'success' ? <Check className="me-2 fs-5" /> : 
                           alertMessage.type === 'danger' ? <X className="me-2 fs-5" /> : 
                           <ExclamationCircle className="me-2 fs-5" />}
                          <span className="fw-medium">{alertMessage.message}</span>
                        </div>
                      </Alert>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Modal de réponse */}
        <Modal show={showResponseModal} onHide={() => setShowResponseModal(false)} size="lg" centered backdrop="static">
          <Modal.Header closeButton className={apiResponse?.id ? "bg-success text-white" : "bg-danger text-white"}>
            <Modal.Title>
              {apiResponse?.id ? (
                <><CheckCircle className="me-2" size={24} />Activité créée avec succès !</>
              ) : (
                <><ExclamationCircle className="me-2" size={24} />Erreur lors de la création</>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {apiResponse?.id ? (
              <>
                <div className="mb-4 p-3 bg-success bg-opacity-10 rounded border border-success">
                  <h5 className="text-success mb-3"><CheckCircle className="me-2" size={20} />Activité enregistrée</h5>
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <tbody>
                        <tr className="table-light">
                          <td className="fw-semibold">ID de l'activité</td>
                          <td><code>{apiResponse.id}</code></td>
                        </tr>
                        <tr>
                          <td className="fw-semibold">Nom</td>
                          <td>{apiResponse.name}</td>
                        </tr>
                        <tr className="table-light">
                          <td className="fw-semibold">Description</td>
                          <td>{apiResponse.description || "—"}</td>
                        </tr>
                        <tr>
                          <td className="fw-semibold">Catégorie</td>
                          <td><Badge bg="primary">{selectedParentCategory?.label}</Badge></td>
                        </tr>
                        <tr className="table-light">
                          <td className="fw-semibold">Date de création</td>
                          <td><Calendar className="me-1" size={14} />{new Date().toLocaleDateString('fr-FR')}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <Alert variant="danger">
                <ExclamationCircle className="me-2" />
                {globalError || apiResponse?.message || "Une erreur est survenue"}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowResponseModal(false)}>
              Fermer
            </Button>
            {apiResponse?.id && (
              <Button variant="success" onClick={() => {
                setShowResponseModal(false);
                navigate("/agent/contribuables/Activites");
              }}>
                <Briefcase className="me-2" />
                Voir la liste
              </Button>
            )}
            {apiResponse?.id && (
              <Button variant="primary" onClick={() => {
                setShowResponseModal(false);
                setFormData({
                  name: "",
                  description: "",
                  code_category: ""
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}>
                <PlusCircle className="me-2" />
                Nouvelle activité
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>

      <style>{`
        .form-control:focus, .form-select:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }
        .progress-bar {
          transition: width 0.5s ease;
        }
        .react-select-container {
          width: 100%;
        }
        .react-select__control {
          background-color: #f8f9fa !important;
          border-color: #dee2e6 !important;
        }
        .react-select__control:hover {
          border-color: #0d6efd !important;
        }
        .react-select__control--is-focused {
          border-color: #0d6efd !important;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25) !important;
        }
        .react-select__option {
          font-size: 0.9rem;
        }
        .react-select__option--is-focused {
          background-color: #e7f1ff !important;
        }
        .breadcrumb {
          background: transparent;
          padding: 0;
        }
        .breadcrumb-item a {
          color: #0d6efd;
          text-decoration: none;
        }
        .breadcrumb-item a:hover {
          text-decoration: underline;
        }
      `}</style>
    </React.Fragment>
  );
}